import { useEffect, useMemo, useState } from "react";

import { useHistory } from "react-router-dom";
import { LoadingButton as MUILoadingButton } from "@mui/lab";
import { Alert, AlertTitle, Box, Button, Tooltip } from "@mui/material";
import capitalize from "lodash/capitalize";

import { errorsText } from "../../../../assets/texts/CloudAnalytics";
import LoadingButton from "../../../../Components/LoadingButton";
import { purifiedHtml } from "../../../../Components/SafeHtml";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { navigatorSubscriptionURL } from "../../../../utils/common";
import { ErrorCode } from "../../utilities";

type Props = {
  disableCacheBtn: boolean;
  errorCode: string;
  errorMessage?: string;
  isReportCachedBtnLoading: boolean;
  onCacheReportClicked: () => void;
};

export function ErrorAlerts({
  onCacheReportClicked,
  disableCacheBtn,
  errorCode,
  errorMessage,
  isReportCachedBtnLoading,
}: Props) {
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [list, setList] = useState<string[]>([]);
  const [showExpertInquiry, setShowExpertInquiry] = useState(false);
  const { userOrganization } = useCustomerContext();
  const history = useHistory();

  const warning =
    errorCode === ErrorCode.EMPTY ||
    errorCode === ErrorCode.EMPTY_DUE_TO_TREND ||
    errorCode === ErrorCode.QUERY_TIMEOUT_524 ||
    errorCode === ErrorCode.FAILED_TO_CACHE;
  const entitlementError = errorCode === ErrorCode.ENTITLEMENT_NOT_ENABLED;
  const severity = entitlementError || errorCode === ErrorCode.EMPTY ? "info" : warning ? "warning" : "error";
  const organizationName = userOrganization?.data.name;

  const tooltipTitle = disableCacheBtn ? errorsText.NOT_SAVED_TOOLTIP : "";

  const showCacheReportButton = errorCode === ErrorCode.QUERY_TIMEOUT_524 || errorCode === ErrorCode.FAILED_TO_CACHE;

  const handleExpertInquiry = () => {
    history.push("./support?open-case-iq=true");
  };

  useEffect(() => {
    setTitle("");
    setList([]);
    setShowExpertInquiry(false);
    switch (errorCode) {
      case ErrorCode.ENTITLEMENT_NOT_ENABLED:
        if (errorMessage) {
          setDescription(errorMessage);
        } else {
          setDescription(errorsText.ENTITLEMENT_NOT_ENABLED.DESCRIPTION);
          setShowExpertInquiry(true);
        }
        break;
      case ErrorCode.OUTSIDE_ORGANIZATION:
        setDescription(errorsText.OUTSIDE_ORGANIZATION.DESCRIPTION);
        setShowExpertInquiry(true);
        break;
      case ErrorCode.EMPTY:
        setDescription(errorsText.EMPTY.DESCRIPTION);
        break;
      case ErrorCode.SERIES_TOO_LARGE:
        setDescription(errorsText.SERIES_TOO_LARGE.DESCRIPTION);
        break;
      case ErrorCode.TOO_LARGE:
        setDescription(errorsText.TOO_LARGE.DESCRIPTION);
        break;
      case ErrorCode.QUERY_TIMEOUT:
        setDescription(errorsText.QUERY_TIMEOUT.DESCRIPTION);
        break;
      case ErrorCode.EMPTY_DUE_TO_TREND:
        setDescription(errorsText.EMPTY_DUE_TO_TREND.DESCRIPTION);
        break;
      case ErrorCode.QUERY_TIMEOUT_524:
        setDescription(errorsText.QUERY_TIMEOUT_524.DESCRIPTION);
        setTitle(errorsText.QUERY_TIMEOUT_524.TITLE);
        break;
      case ErrorCode.EXCEEDED_FIRESTORE_LIMIT:
        setDescription(errorsText.EXCEEDED_FIRESTORE_LIMIT.DESCRIPTION);
        setShowExpertInquiry(true);
        break;
      case ErrorCode.CALCULATED_METRIC_NOT_PROVIDED:
        setDescription(errorsText.NO_ACCESS_METRIC);
        setShowExpertInquiry(true);
        break;
      case ErrorCode.EXTENDED_METRIC_NOT_PROVIDED:
        setDescription(errorsText.NO_ACCESS_METRIC_EXTENDED);
        setShowExpertInquiry(true);
        break;
      case ErrorCode.BQ_PROXY_NOT_FOUND:
        setDescription(errorsText.BQ_PROXY_NOT_FOUND.DESCRIPTION);
        setShowExpertInquiry(true);
        break;
      case ErrorCode.BQ_PROXY_FORBIDDEN:
        setDescription(errorsText.BQ_PROXY_FORBIDDEN.DESCRIPTION);
        setShowExpertInquiry(true);
        break;
      case ErrorCode.BQ_PROXY_IAM_PERMISSION_DENIED:
        setDescription(errorsText.BQ_PROXY_IAM_PERMISSION_DENIED.DESCRIPTION);
        setShowExpertInquiry(true);
        break;
      case ErrorCode.FAILED_TO_CACHE:
        setDescription(errorsText.FAILED_TO_CACHE.DESCRIPTION);
        setTitle(errorsText.FAILED_TO_CACHE.TITLE);
        break;
      default:
        setDescription(errorsText.DEFAULT.DESCRIPTION);
        setShowExpertInquiry(true);
        break;
    }
  }, [errorCode, errorMessage, organizationName]);

  const descriptionElement = useMemo(
    // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
    () => <div dangerouslySetInnerHTML={{ __html: purifiedHtml(description) ?? "" }} />,
    [description]
  );

  const cacheReportButton = showCacheReportButton ? (
    <Tooltip title={tooltipTitle} disableHoverListener={!disableCacheBtn}>
      <span>
        <MUILoadingButton
          disabled={disableCacheBtn}
          loading={isReportCachedBtnLoading}
          onClick={onCacheReportClicked}
          color="inherit"
          size="small"
        >
          <span>{errorsText.CACHE_REPORT_BTN}</span>
        </MUILoadingButton>
      </span>
    </Tooltip>
  ) : null;

  const expertInquiryButton = showExpertInquiry ? (
    <Button onClick={handleExpertInquiry} color="inherit" size="small" data-cy="error-expert-inquiry-button">
      {errorsText.EXPERT_INQUIRY}
    </Button>
  ) : null;

  return (
    <Alert variant="standard" severity={severity} action={cacheReportButton || expertInquiryButton}>
      <AlertTitle>{title ?? capitalize(severity)}</AlertTitle>
      {descriptionElement}
      {list.length > 0 && (
        <ul>
          {list.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      )}
      {entitlementError && (
        <Box
          sx={{
            pt: 1,
          }}
        >
          <LoadingButton
            href={navigatorSubscriptionURL}
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            loading={false}
            mixpanelEventId="analytics.reports.tiers.learn_more"
            eventProperties={{ message: errorMessage }}
          >
            {errorsText.LEARN_MORE}
          </LoadingButton>
        </Box>
      )}
    </Alert>
  );
}
